import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Heading,
  Flex,
  Button,
  Select,
  useDisclosure,
  useToast,
  Checkbox,
} from "@chakra-ui/react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "react-calendar/dist/Calendar.css";
import {
  getAddress,
  postBooking,
  postPayment,
  getBusinessSetting,
  getWallet,
} from "../../api-services";
import ServiceDetails from "../../components/ServiceDetails";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ManageAddress from "../../model/ManageAddress";
import PaymentComponent from "../../components/PaymentGatway";
import ProviderComponent from "../../components/Provider";
import { useLocation } from "react-router-dom";
import moment from "moment";
import BookingTimeValidator from './BookingValidator';

const convertDateTime = (dateTimeString) => {
  const dateTime = moment(dateTimeString);

  const bookingDate = dateTime.format("YYYY-MM-DD");
  const bookingTime = dateTime.format("HH:mm");

  return {
    booking_date: bookingDate,
    booking_time: bookingTime,
  };
};

const providerTypes = [
  { id: "freelancer", name: "Freelancer" },
  { id: "company", name: "Company" },
];

const BookingPage = () => {
  const { redDark } = useSelector((state) => state.theme);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const paymentMethod = searchParams.get("paymentMode");
  const chooseProvider = searchParams.get("chooseProvider");
  const selectId = searchParams.get("id");

  const {
    isOpen: isAddressOpen,
    onOpen: onAddressOpen,
    onClose: onAddressClose,
  } = useDisclosure();

  const [calendarDate, setCalendarDate] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [tax, setTax] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [businessSetting, setBusinessSetting] = useState([]);
  const [walletAmt, setWalletAmt] = useState(0);
  const [addressId, setAddressId] = useState(null);
  const [bookindData, setBookingData] = useState(null);
  const [bookindConfirmedData, setBookingConfirmedData] = useState(null);

  const [selectedProvider, setSelectedProvider] = useState("");
  const [providerOpen, setProviderOpen] = useState(false);
  const [selectedProviderData, setSelectedProviderData] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isWalletChecked, setIsWalletChecked] = useState(false);
  const [isAutoMatch, setIsAutoMatch] = useState(true);

  // Validation errors
  const [addressError, setAddressError] = useState("");
  const [dateError, setDateError] = useState("");

  // Get today's date for minimal selectable date
  const today = new Date();
  // Ensure we can't select dates in the past
  const minSelectableDate = new Date();

  const timeValidator = new BookingTimeValidator();

  const handleChangeAddress = (e) => {
    setAddressId(e.target.value);
    setAddressError(""); // Clear error when valid input is provided
  };

  useEffect(() => {
    let provider;
    chooseProvider == "freelancer_prices"
      ? (provider = "freelancer")
      : (provider = "company");
    setSelectedProvider(provider);
  }, [chooseProvider]);

  // Effect to check if calendar date is still valid when service duration changes
  useEffect(() => {
    if (calendarDate && bookindData?.duration) {
      // Validate the selected time
      const selectedTime = moment(calendarDate);
      const serviceHours = bookindData.duration;
      const hour = selectedTime.hour();

      // If service would extend past midnight
      const maxHour = 24 - serviceHours;
      if (hour > maxHour) {
        // Reset the time to the latest valid time for this service duration
        const newDate = new Date(calendarDate);
        newDate.setHours(maxHour, 0, 0, 0);
        setCalendarDate(newDate);

        toast({
          title: "Booking time adjusted",
          description: `Due to service duration of ${serviceHours} hours, the latest booking time is ${maxHour}:00`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [bookindData?.duration, calendarDate, toast]);

  // Update duration when time changes
  useEffect(() => {
    if (calendarDate && bookindData?.duration) {
      const hour = calendarDate.getHours();
      const currentDuration = parseInt(bookindData.duration);
      
      // Check if current duration is valid for selected time
      if (!timeValidator.isDurationValidForTime(currentDuration, hour)) {
        // Find the first valid duration for this time
        const availableDurations = timeValidator.getAvailableDurations(hour);
        if (availableDurations.length > 0) {
          // Update the duration to the first available option
          setBookingData(prev => ({
            ...prev,
            duration: availableDurations[0]
          }));
          
          toast({
            title: "Duration adjusted",
            description: `For bookings after ${hour}:00, available durations are: ${availableDurations.join(', ')} hours`,
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    }
  }, [calendarDate, bookindData?.duration]);

  const handleProviderChange = (event) => {
    if (typeof event !== "string") {
      setSelectedProvider(event?.target?.value);
      setSelectedProviderData(null);
      if (event?.target?.value !== "") {
        if (selectedProvider === "company" && !calendarDate) {
          setErrorState("Please choose a date first.");
          return;
        }
        setProviderOpen(true);
      }
    }
    if (typeof event == "string" && event !== "") {
      setSelectedProvider(event);
      if (selectedProvider === "company" && !calendarDate) {
        setErrorState("Please choose a date first.");
        return;
      }
      setProviderOpen(true);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const now = moment();

    if (!addressId) {
      setAddressError("Address is required.");
      isValid = false;
    }

    if (calendarDate == null) {
      setDateError("Date is required.");
      isValid = false;
    } else {
      const selectedTime = moment(calendarDate);
      const serviceHours = bookindData?.duration || 0;

      // Booking must be at least 3 hours ahead of now
      if (selectedTime.diff(now, 'hours', true) < 3) {
        setDateError("Booking must be at least 3 hours in advance.");
        isValid = false;
      }

      // Booking must be within business hours
      const hour = selectedTime.hour();
      const minute = selectedTime.minute();

      if (hour < 6 || (hour === 21 && minute > 0) || hour > 21) {
        setDateError("Booking time must be between 06:00 AM and 09:00 PM.");
        isValid = false;
      }

      // Service can't extend past midnight
      const serviceEndHour = hour + serviceHours;
      if (serviceEndHour > 24) {
        setDateError(`For ${serviceHours}-hour services, booking must be before or at ${24 - serviceHours}:00.`);
        isValid = false;
      }
    }

    // Provider related validations
    if (
      paymentMethod === "online" &&
      selectedProviderData == null &&
      selectedProvider === "freelancer" &&
      !isAutoMatch
    ) {
      setErrorState(`Choose provider manually or auto match.`);
      isValid = false;
    }

    if (
      paymentMethod === "online" &&
      bookindData?.qty !== selectedProviderData?.length &&
      selectedProvider === "freelancer" &&
      !isAutoMatch
    ) {
      setErrorState(`Please select providers.`);
      isValid = false;
    }

    if (
      paymentMethod === "online" &&
      bookindData?.qty !== selectedProviderData?.length &&
      selectedProvider === "company"
    ) {
      setErrorState(`Please select premium and manpower.`);
      isValid = false;
    }

    return isValid;
  };

  const bookNow = () => {
    if (!validateForm()) return;
    setAddressError("");
    setErrorState("");
    handleConfirm();
    setDateError("");
  };

  const handleConfirm = async () => {
    let provider;
    chooseProvider == "freelancer_prices"
      ? (provider = "freelancer")
      : (provider = "company");

    setConfirming(true);

    const { booking_date, booking_time } = convertDateTime(calendarDate);
    const payload = {
      services: [bookindData],
      business_id:
        provider === "freelancer"
          ? selectedProviderData || []
          : selectedProviderData?.[0] || [],
      count_employee:
        provider === "freelancer" ? [] : selectedProviderData?.[1] || [],
      booking_date: booking_date,
      booking_time: booking_time,
      payment_mode:
        isWalletChecked && walletAmt.wallet_amount >= grandTotal
          ? "wallet"
          : paymentMethod,
      address_id: Number(addressId),
      is_wallet: isWalletChecked,
      booking_type: provider,
      total_manpower: bookindData?.qty,
    };
    try {
      const res = await postBooking(payload);
      if (res?.success === false) {
        toast({
          title: "Error",
          description: res?.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        if (paymentMethod === "online") {
          setBookingConfirmedData(res?.response);
          setIsPaymentOpen(true);
        } else {
          toast({
            title: "Success",
            description: res?.message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          setTimeout(() => {
            setConfirming(false);
            navigate("/customer-booking-history");
          }, 1000);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const handleAutoMatch = async () => {
    if (!isAutoMatch) setSelectedProviderData(null);
    setIsAutoMatch((prev) => !prev);
  };

  const paymentPost = async (razorpayPaymentId) => {
    try {
      const payload = {
        payment_id: razorpayPaymentId,
        payment_status: "success",
      };
      const res = await postPayment(payload, bookindConfirmedData?.booking_id);
      setIsPaymentOpen(false);
      if (res?.success === false) {
        toast({
          title: "Error",
          description: res?.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description: res?.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setTimeout(() => {
          setConfirming(false);
          navigate("/customer-booking-history");
        }, 1000);
      }
    } catch (error) {
      setError("Failed to process payment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchBusinessSetting();
    fetchWalletData();
  }, []);

  const fetchData = async () => {
    setError("");
    try {
      const res = await getAddress();
      setAddresses(res?.response || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletData = async () => {
    setError("");
    try {
      const res = await getWallet();
      setWalletAmt(res || {});
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessSetting = async () => {
    setError("");
    try {
      const res = await getBusinessSetting();
      setBusinessSetting(res?.response || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTotalsChange = (total, taxA, grandTotal, tax, allData) => {
    setTax(tax);
    setBookingData(allData);
    setTotalAmount(total);
    setTaxAmount(taxA);
    setGrandTotal(grandTotal);

    // Reset calendar date when service changes to ensure proper validation
    setCalendarDate(null);
    setDateError("");
  };

  const onAddressCloseHandlers = () => {
    onAddressClose();
    fetchData();
  };

  const selectProvider = (data) => {
    setSelectedProviderData(data);
    setProviderOpen(false);
    if (data?.length > 0) {
      setErrorState(null);
      setIsAutoMatch(false);
    }
    if (data[0]?.length > 0) {
      setErrorState(null);
    }
  };

  const closeProvider = () => {
    setProviderOpen(false);
  };

  const handlePaymentSuccess = (response) => {
    paymentPost(response);
    setIsPaymentOpen(false);
  };

  const handlePaymentFailure = (response) => {
    toast({
      title: "Payment Failed",
      description: "Your payment could not be processed",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    setIsPaymentOpen(false);
  };

  const handlePaymentCancel = () => {
    toast({
      title: "Payment Cancelled",
      description: "You've cancelled the payment process",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
    setIsPaymentOpen(false);
  };

  const handleCheckboxChange = (e) => {
    setIsWalletChecked(e.target.checked);
  };

  const getFilteredTimes = (date, serviceHours) => {
    // Add console logs to debug
    console.log("Filter function called with:", { date, serviceHours });

    if (!date) {
      console.log("No date provided, returning false");
      return () => false;
    }

    // Make sure serviceHours is a number - very important!
    let duration = 3; // Default to 3 hours
    if (serviceHours) {
      // Try to parse as number if it's a string
      if (typeof serviceHours === 'string') {
        duration = parseInt(serviceHours, 10);
        if (isNaN(duration)) duration = 3;
      } else if (typeof serviceHours === 'number') {
        duration = serviceHours;
      }
    }

    console.log("Using duration:", duration);

    const now = new Date();

    // Compare just the date portions
    const selectedDate = new Date(date);
    const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Is this today's date?
    const isToday = selectedDay.getTime() === today.getTime();
    console.log("Is today?", isToday);

    // Return the actual filter function
    return (time) => {
      const hour = time.getHours();
      const minute = time.getMinutes();

      let result = true;
      let reason = "";

      // 1. Basic business hours check (6 AM to 9 PM)
      if (hour < 6 || hour > 21 || (hour === 21 && minute > 0)) {
        result = false;
        reason = "Outside business hours";
      }

      // 2. Check if service would run past midnight
      else if (hour + duration > 24) {
        result = false;
        reason = "Would extend past midnight";
      }

      // 3. For today, enforce 3-hour advance booking
      else if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        // Must be at least 3 hours from now for same-day bookings
        if (hour < currentHour + 3) {
          result = false;
          reason = "Less than 3 hours advance";
        }
      }

      // 4. Apply specific duration-based restrictions for 9, 6, and 3 hour services
      else if (duration === 9 && hour >= 15) {
        result = false;
        reason = "9-hour service must start before 3 PM";
      }
      else if (duration === 6 && hour >= 18) {
        result = false;
        reason = "6-hour service must start before 6 PM";
      }
      else if (duration === 3 && hour >= 21) {
        result = false;
        reason = "3-hour service must start before 9 PM";
      }

      // Log the result for debugging specific times
      if (hour === 12 || hour === 14 || hour === 17) {
        console.log(`Time ${hour}:${minute} => ${result} (${reason})`);
      }

      return result;
    };
  };

  // Simplified date change handler
  const handleDateChange = (date) => {
    setCalendarDate(date);
    setDateError(""); // Clear error by default

    if (!date) return;

    const now = new Date();
    let serviceHours = 3; // Default

    // Safely parse service hours
    if (bookindData?.duration) {
      if (typeof bookindData.duration === 'string') {
        serviceHours = parseInt(bookindData.duration, 10);
        if (isNaN(serviceHours)) serviceHours = 3;
      } else if (typeof bookindData.duration === 'number') {
        serviceHours = bookindData.duration;
      }
    }

    const selectedHour = date.getHours();

    // Compare dates
    const selectedDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isToday = selectedDay.getTime() === today.getTime();

    // Only validate if we have all the data
    if (selectedHour && serviceHours) {
      let hasError = false;
      let errorMsg = "";

      // Check today and 3 hour advance
      if (isToday && selectedHour < now.getHours() + 3) {
        hasError = true;
        errorMsg = "Please select a time at least 3 hours from now";
      }
      // Check if duration is valid for selected time
      else if (!timeValidator.isDurationValidForTime(serviceHours, selectedHour)) {
        hasError = true;
        const availableDurations = timeValidator.getAvailableDurations(selectedHour);
        errorMsg = `For bookings at ${selectedHour}:00, available durations are: ${availableDurations.join(', ')} hours`;
      }
      // Check if service would extend past midnight
      else if (selectedHour + serviceHours > 24) {
        hasError = true;
        errorMsg = `For ${serviceHours}-hour services, booking must be before ${24 - serviceHours}:00`;
      }

      if (hasError) {
        setDateError(errorMsg);
      }
    }
  };

  // DIAGNOSTIC effect to log important state changes
  useEffect(() => {
    console.log("Calendar date changed:", calendarDate);
    console.log("Booking data:", bookindData);
  }, [calendarDate, bookindData]);

  return (
    <>
      <Box
        maxW="container.xxl"
        mx="auto"
        px={{ base: "10px", md: "30px" }}
        pt="60px"
        pb="30px"
        mt="60px"
      >
        <Flex direction={{ base: "column", lg: "row" }}>
          <Box
            maxW={{ base: "100%", lg: "1200px" }}
            mx="auto"
            w="100%"
            p={4}
            mt={{ base: 4, lg: 0 }}
            bg="gray.100"
            borderRadius="md"
            boxShadow="lg"
            position="relative"
          >
            <Box mt={6} p={4} bg="white" borderRadius="md" boxShadow="md">
              <Flex justifyContent="space-between" alignItems="center" mb={3}>
                <Heading as="h5" size="md">
                  Service Details
                </Heading>
                {paymentMethod === "cod" ? null : (
                  <Checkbox
                    size="md"
                    isChecked={isWalletChecked}
                    onChange={handleCheckboxChange}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {" "}
                      Use Wallet : ₹ {walletAmt.wallet_amount}
                    </span>
                  </Checkbox>
                )}
              </Flex>
              <ServiceDetails
                onTotalsChange={handleTotalsChange}
                chooseProvider={chooseProvider}
                businessTax={businessSetting?.tax}
                plateformTax={businessSetting?.admin_fees}
                calendarDate={calendarDate}
              />
              <Box mt={6}>
                <DatePicker
                  selected={calendarDate}
                  onChange={handleDateChange}
                  filterTime={(time) => {
                    // Get the duration safely
                    let duration = 3; // Default
                    if (bookindData?.duration) {
                      if (typeof bookindData.duration === 'string') {
                        duration = parseInt(bookindData.duration, 10);
                        if (isNaN(duration)) duration = 3;
                      } else if (typeof bookindData.duration === 'number') {
                        duration = bookindData.duration;
                      }
                    }

                    // Log what we're passing to the filter
                    console.log("Filtering with duration:", duration);

                    // Call the filter with proper parameters
                    return getFilteredTimes(calendarDate, duration)(time);
                  }}
                  timeIntervals={60} // Only full hours
                  showTimeSelect
                  dateFormat="Pp"
                  className="calendarPop"
                  minDate={new Date()} // This allows today's date
                  placeholderText="Pick the Date and Time"
                />
                {dateError && (
                  <Text color="red" mt={2}>
                    {dateError}
                  </Text>
                )}
                {bookindData?.duration && (
                  <Text color="blue.600" fontSize="sm" mt={1}>
                    For {bookindData.duration}-hour services, latest booking time is {24 - bookindData.duration}:00
                  </Text>
                )}
              </Box>
              <Text fontWeight="bold" mt={4} mb="10px">
                Total Amount: ₹{totalAmount}
              </Text>
              <Text fontWeight="bold" color="red" mb="10px">
                Tax: {businessSetting?.tax ?? 0}%
              </Text>
              <Text fontWeight="bold" color="red" mb="10px">
                SGST : {businessSetting?.sgst}%
              </Text>
              <Text fontWeight="bold" color="red" mb="10px">
                CGST : {businessSetting?.cgst ?? 0}%
              </Text>
              <Text fontWeight="bold" mb="10px">
                Platform fees: {businessSetting?.admin_fees || 0}%
              </Text>
              <Text fontWeight="bold" color={redDark} mb="10px">
                Grand Total: ₹{grandTotal || 0}
              </Text>
            </Box>

            <Box mt={6}>
              <Select
                onChange={handleChangeAddress}
                value={addressId}
                placeholder="Select address"
                borderRadius="5px"
              >
                {addresses.map((address, index) => {
                  return (
                    <option value={address?.id} key={index}>
                      {address.address_line_one}{" "}
                      {`${address.city}, ${address.state} - ${address.pincode}`}
                    </option>
                  );
                })}
              </Select>
              {addressError && (
                <Text color="red" mt={2}>
                  {addressError}
                </Text>
              )}
            </Box>
            <br />
            {paymentMethod === "online" && (
              <Box
                display="flex"
                gap="8px"
                flexDirection="column"
              >
                <Box
                  display="flex"
                  alignItems="flex-end"
                  gap="8px"
                  borderBottom="1px solid #0101011f"
                >
                  <Text
                    colorScheme="#1E88E51f"
                    variant="outline"
                    fontSize="1.1rem"
                    fontWeight="600"
                  >
                    {selectedProvider === "company" ? "Premium" : "Standard"}{" "}
                  </Text>

                  {selectedProvider != "" && (
                    <Button
                      variant="outline"
                      colorScheme="#1E88E51f"
                      style={{
                        margin: "5px",
                        cursor: "pointer",
                        color: "#1E88E5",
                        fontSize: "14px",
                      }}
                      onClick={() => handleProviderChange(selectedProvider)}
                    >
                      Pick Your Pro.{" "}
                    </Button>
                  )}
                  {errorState && (
                    <Text color="red" mt={2}>
                      {errorState}
                    </Text>
                  )}
                </Box>
                {selectedProvider !== "company" && (
                  <Box>
                    {selectedProvider !== "" && (
                      <Checkbox
                        size="md"
                        isChecked={isAutoMatch}
                        onChange={handleAutoMatch}
                      >
                        Auto Match
                      </Checkbox>
                    )}
                  </Box>
                )}
              </Box>
            )}
            <br />
            <Button
              variant="outline"
              onClick={onAddressOpen}
              border="1px solid"
              fontSize="14px"
              color="#010101"
              _hover={{
                bg: "#1E88E51f",
                border: "1px solid #1E88E5",
                color: "#1E88E5",
              }}
            >
              Add new Address
            </Button>
            <br />
            <br />
            <br />
            <br />
            <Text fontWeight="600">
            PLEASE NOTE: Once a booking is confirmed, it can be canceled with cancellation fees
            </Text>
            <Button
              mt={6}
              colorScheme="teal"
              onClick={bookNow}
              isLoading={confirming}
              loadingText="Confirming"
            >
              Book Now
            </Button>
          </Box>
        </Flex>
      </Box>

      <Text color="red" mt={2}>
        {error}
      </Text>
      <Text color="green" mt={2}>
        {message}
      </Text>

      <ManageAddress
        isOpen={isAddressOpen}
        onClose={() => onAddressCloseHandlers()}
      />

      {providerOpen && (
        <ProviderComponent
          isOpen={providerOpen}
          onClose={closeProvider}
          type={selectedProvider}
          selectProviders={selectProvider}
          totalProvider={bookindData?.qty}
          viewSelected={selectedProviderData}
          favSelected={[parseInt(selectId)]}
          selectedDate={calendarDate}
        />
      )}

      {isPaymentOpen && (
        <PaymentComponent
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          onCancel={handlePaymentCancel}
          data={bookindConfirmedData}
          mode={businessSetting.payment_mode}
        />
      )}
    </>
  );
};

export default BookingPage;